import { useState, useEffect } from "react";
import { ArrowLeft, AlertTriangle, CloudLightning } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { getSeasonStorms } from "@/lib/stormsQueries";

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * SeasonDetailPage – shows the list of named storms for a given season.
 *
 * Can be used in two ways:
 *  1. Inline (from NamedStormsPage): pass `season` and `onBack` props.
 *  2. Standalone route (/storms/season/:season): pass `standalone` prop;
 *     season is read from URL params and back navigates via the router.
 *
 * Optional prop:
 *  `onSelectStorm(stormName)` – called when the user clicks a storm row.
 *   When provided, storm rows become interactive buttons.
 */
export default function SeasonDetailPage({ season: seasonProp, onBack, standalone, onSelectStorm }) {
  const { season: seasonParam } = useParams();
  const navigate = useNavigate();
  const season = standalone ? decodeURIComponent(seasonParam ?? "") : seasonProp;

  const [storms, setStorms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!season) return;
    setLoading(true);
    getSeasonStorms(season)
      .then(setStorms)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [season]);

  function handleBack() {
    if (standalone) {
      navigate("/storms/seasons");
    } else if (onBack) {
      onBack();
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 w-48 bg-[#111827] border border-white/10" />
        <div className="h-48 bg-[#111827] border border-white/10" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-sm">Failed to load season: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-cyan-400 transition-colors mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          All Seasons
        </button>
        <h2 className="text-2xl font-bold text-[#F9FAFB]">{season} Season</h2>
        <p className="text-sm text-[#6B7280] mt-1">
          {storms.length} named storm{storms.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Storms table */}
      <div className="border border-white/10 bg-[#111827] overflow-hidden">
        <div className="grid grid-cols-[2fr_2fr] gap-4 px-4 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-[#6B7280]">
          <span>Name</span>
          <span>Date Named</span>
        </div>

        {storms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#6B7280]">
            <CloudLightning className="w-10 h-10 opacity-30" />
            <p className="text-sm">
              There are no named storms for this {season} storm season.
            </p>
          </div>
        ) : (
          storms.map((s) => (
            <div
              key={s.id}
              onClick={onSelectStorm ? () => onSelectStorm(s.storm_name) : undefined}
              className={`grid grid-cols-[2fr_2fr] gap-4 px-4 py-3 border-b border-white/5 last:border-0 ${
                onSelectStorm ? "cursor-pointer hover:bg-white/[0.03] transition-colors" : ""
              }`}
            >
              <div>
                <span className={`text-sm font-medium ${onSelectStorm ? "text-cyan-400" : "text-[#F9FAFB]"}`}>
                  {s.storm_name}
                </span>
                {s.notes ? (
                  <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">{s.notes}</p>
                ) : null}
              </div>
              <span className="text-sm text-[#9CA3AF] self-center">{fmt(s.date_named)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

